import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BuyButton } from "@/components/BuyButton";
import { Seo } from "@/components/Seo";
import { ArrowLeft, ArrowRight, PlayCircle, FileText, Check, Clock, BarChart3, Lock, Star, HelpCircle } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const CAT_LABEL = { engineering: "Engineering", trading: "Trading", school: "School 1–12", mba: "MBA Finance" };
const BRANCH_LABEL = { common: "All Branches", mechanical: "Mechanical", diploma: "Mech Diploma", civil: "Civil", computer: "Computer/IT", electrical: "Electrical", ec: "E & C", chemical: "Chemical" };
const BOARD_LABEL = { gseb: "GSEB", cbse: "CBSE", icse: "ICSE" };

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6">
        <Header />
        <p className="font-display text-3xl font-bold" data-testid="course-not-found">Course not found.</p>
        <Link to="/" className="link-underline text-sm uppercase tracking-widest text-brand">Back to home</Link>
      </div>
    );
  }

  if (!course) {
    return <div className="flex min-h-screen items-center justify-center text-zinc-600" data-testid="course-loading">Loading course...</div>;
  }

  return (
    <div data-testid="course-detail-page">
      <Seo
        title={`${course.title} | PP Academy`}
        description={course.tagline || course.description?.slice(0, 150)}
        path={`/course/${course.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.description,
          provider: { "@type": "EducationalOrganization", name: "PP Academy", sameAs: "https://ppacedemy.in" },
          offers: {
            "@type": "Offer",
            price: course.price,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: `https://ppacedemy.in/course/${course.slug}`,
          },
          hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: course.duration || "Self-paced" },
        }}
      />
      <Header />
      <section className="px-6 pb-24 pt-32 md:px-12 md:pt-40">
        <div className="mx-auto max-w-[1600px]">
          <Link to="/" data-testid="back-to-courses-link" className="mb-10 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-zinc-500 hover:text-white">
            <ArrowLeft size={14} /> All Courses
          </Link>
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="lg:col-span-7">
              <p className="mb-4 text-xs font-medium uppercase tracking-[0.4em] text-brand">
                {CAT_LABEL[course.category]}
                {course.branch ? ` · ${BRANCH_LABEL[course.branch] || course.branch}` : ""}
                {course.board ? ` · ${BOARD_LABEL[course.board] || course.board}` : ""}
              </p>
              <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="course-title">
                {course.title}
              </h1>
              <p className="mt-6 font-editorial text-xl italic leading-relaxed text-zinc-400 md:text-2xl">{course.tagline}</p>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">{course.description}</p>
              <div className="mt-8 flex flex-wrap items-center gap-6 border-y border-white/10 py-5 text-sm text-zinc-400">
                <span className="flex items-center gap-2"><Clock size={15} className="text-brand" /> {course.duration}</span>
                <span className="flex items-center gap-2"><PlayCircle size={15} className="text-brand" /> {course.bundle ? `${course.bundle_courses?.length || 0} subjects` : `${course.lessons.length} lessons`}</span>
                <span className="flex items-center gap-2"><BarChart3 size={15} className="text-brand" /> {course.level}</span>
              </div>
              <div className="mt-10 overflow-hidden border border-white/10">
                <img src={course.image} alt={course.title} className="aspect-[16/8] w-full object-cover opacity-85" data-testid="course-image" />
              </div>
              <h2 className="mt-16 font-display text-2xl font-black uppercase tracking-tighter md:text-3xl">What you'll walk away with</h2>
              <ul className="mt-8 flex flex-col gap-4" data-testid="course-outcomes">
                {course.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-base text-zinc-300">
                    <Check size={18} className="mt-0.5 shrink-0 text-brand" /> {o}
                  </li>
                ))}
              </ul>
              {course.bundle && course.bundle_subjects?.length > 0 && (
                <>
                  <h2 className="mt-16 font-display text-2xl font-black uppercase tracking-tighter md:text-3xl">Everything in this bundle</h2>
                  <p className="mt-3 text-sm text-zinc-500">One payment unlocks every subject below — each with its own dashboard, chapters, self-tests and question bank.</p>
                  <div className="mt-8 divide-y divide-white/10 border border-white/10" data-testid="bundle-subjects">
                    {course.bundle_subjects.map((s, i) => (
                      <Link key={s.slug} to={`/course/${s.slug}`} data-testid={`bundle-subject-${i}`} className="group flex items-center justify-between gap-4 px-6 py-5 transition-colors duration-300 hover:bg-white/5">
                        <div className="flex items-center gap-4">
                          <span className="font-display text-sm font-bold text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                          <span className="text-sm text-zinc-200 md:text-base">{s.title}</span>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="text-xs text-zinc-600 line-through">₹{s.price.toLocaleString("en-IN")}</span>
                          <ArrowRight size={15} className="text-zinc-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="mt-4 border border-brand/30 bg-brand/5 px-6 py-4 text-sm text-zinc-300" data-testid="bundle-savings">
                    Bought separately: <span className="text-zinc-500 line-through">₹{course.mrp.toLocaleString("en-IN")}</span> · Bundle price: <span className="font-display font-black text-brand">₹{course.price.toLocaleString("en-IN")}</span> — you save ₹{(course.mrp - course.price).toLocaleString("en-IN")}
                  </div>
                </>
              )}
              {course.lessons.length > 0 && (
              <>
              <h2 className="mt-16 font-display text-2xl font-black uppercase tracking-tighter md:text-3xl">Curriculum</h2>
              <p className="mt-3 text-sm text-zinc-500">Chapter-wise, following your textbook. Lessons marked <span className="font-bold text-emerald-500">FREE</span> can be tried right now — no payment needed.</p>
              <div className="mt-8 divide-y divide-white/10 border border-white/10" data-testid="course-curriculum">
                {course.lessons.map((l, i) =>
                  l.free_preview ? (
                    <Link key={l.id || l.title} to={`/course/${slug}/learn/${l.id}`} data-testid={`curriculum-lesson-${i}`} className="group flex items-center justify-between gap-4 px-6 py-5 transition-colors duration-300 hover:bg-white/5">
                      <div className="flex items-center gap-4">
                        <span className="font-display text-sm font-bold text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-sm text-zinc-200 md:text-base">{l.title}</span>
                        {l.has_quiz && <HelpCircle size={14} className="text-brand" />}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="bg-emerald-500/10 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-emerald-500">Free</span>
                        <span className="text-xs text-zinc-600">{l.duration}</span>
                      </div>
                    </Link>
                  ) : (
                    <div key={l.id || l.title} className="flex items-center justify-between gap-4 px-6 py-5" data-testid={`curriculum-lesson-${i}`}>
                      <div className="flex items-center gap-4">
                        <span className="font-display text-sm font-bold text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-sm text-zinc-300 md:text-base">{l.title}</span>
                        {l.has_quiz && <HelpCircle size={14} className="text-brand" />}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <Lock size={14} className="text-zinc-600" />
                        <span className="text-xs text-zinc-600">{l.duration}</span>
                      </div>
                    </div>
                  )
                )}
              </div>
              </>
              )}
              {course.notes.length > 0 && (
                <>
                  <h2 className="mt-16 font-display text-2xl font-black uppercase tracking-tighter md:text-3xl">Notes & Downloads</h2>
                  <div className="mt-8 flex flex-col gap-3" data-testid="course-notes">
                    {course.notes.map((n) => (
                      <div key={n.title} className="flex items-center gap-3 border border-white/10 px-6 py-4 text-sm text-zinc-300">
                        <FileText size={16} className="text-brand" /> {n.title}
                        <span className="ml-auto text-xs uppercase tracking-widest text-zinc-600">Unlocked after purchase</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {course.question_bank?.length > 0 && (
                <>
                  <h2 className="mt-16 font-display text-2xl font-black uppercase tracking-tighter md:text-3xl">Question Bank</h2>
                  <p className="mt-3 text-sm text-zinc-500">Previous years' exam questions inside this course. <Star size={13} className="inline fill-amber-400 text-amber-400" /> marks frequently repeated ones. Answers unlock after purchase.</p>
                  <div className="mt-8 divide-y divide-white/10 border border-white/10" data-testid="course-qbank">
                    {course.question_bank.map((q, i) => (
                      <div key={q.id || i} className="flex items-start gap-4 px-6 py-5" data-testid={`qbank-item-${i}`}>
                        {q.important ? <Star size={16} className="mt-0.5 shrink-0 fill-amber-400 text-amber-400" /> : <HelpCircle size={16} className="mt-0.5 shrink-0 text-zinc-600" />}
                        <div>
                          <p className="text-sm text-zinc-300 md:text-base">{q.question}</p>
                          <p className="mt-1 text-xs uppercase tracking-widest text-zinc-600">{[q.year, q.topic].filter(Boolean).join(" · ")}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8, ease: EASE }} className="lg:col-span-5">
              <div className="sticky top-28 border border-white/10 bg-[#111111] p-8 md:p-10" data-testid="pricing-card">
                <div className="text-xs uppercase tracking-widest text-zinc-500">One-time payment · Lifetime access</div>
                <div className="mt-4 flex items-baseline gap-4">
                  <span className="font-display text-5xl font-black tracking-tighter" data-testid="course-price">₹{course.price.toLocaleString("en-IN")}</span>
                  <span className="text-lg text-zinc-600 line-through">₹{course.mrp.toLocaleString("en-IN")}</span>
                </div>
                <div className="mt-2 text-xs font-bold uppercase tracking-widest text-emerald-500">
                  Save {Math.round((1 - course.price / course.mrp) * 100)}% today
                </div>
                <div className="mt-8">
                  <BuyButton course={course} />
                </div>
                <ul className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-8">
                  {course.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-zinc-400">
                      <Check size={15} className="shrink-0 text-brand" /> {f}
                    </li>
                  ))}
                </ul>
                <p className="mt-8 text-xs leading-relaxed text-zinc-600">
                  Instant access after payment. Video lessons and PDF notes are delivered to your personal access page.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
